import { useMemo, useState } from 'react';
import { LANGUAGES } from '../../i18n';
import { api, AVAILABLE_FORMATS, AVAILABLE_COLORS, COLOR_HEX } from '../../lib/api';
import { logger } from '../../lib/logger';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Sparkles, ImageIcon, X, FileDown, Upload } from 'lucide-react';

const EMPTY_TR = { name: '', description: '', tagline: '' };
const EMPTY_PRODUCT = {
  sku: '', category: '', tags: [],
  formats: [], colors: [], color_code: '', micron: null, micron_min: null, micron_max: null,
  melting_temp_c: null, melting_temp_min_c: null, melting_temp_max_c: null,
  working_temp_c: null, working_temp_min_c: null, working_temp_max_c: null,
  viscosity_cp: null, viscosity_temp_c: null,
  package_weight_kg: null, package_pieces: null, moq: null,
  images: [], sort_order: null,
  translations: {}, featured: false, active: true,
};

function toBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

export default function ProductsTab({ products, onReload }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_PRODUCT);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLang, setPdfLang] = useState('en');
  const [pdfSelected, setPdfSelected] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Cached active-product list — reused in both the PDF dialog checkbox list and
  // the "Select all" action. Avoids re-filtering on every keystroke / edit.
  const activeProducts = useMemo(() => products.filter(p => p.active), [products]);

  const openNew = () => {
    setEditing({ ...EMPTY_PRODUCT, translations: { it: { ...EMPTY_TR }, en: { ...EMPTY_TR } } });
    setEditorOpen(true);
  };
  const openEdit = (p) => {
    const tr = { ...p.translations };
    LANGUAGES.forEach(l => { if (!tr[l.code]) tr[l.code] = { ...EMPTY_TR }; });
    setEditing({ ...EMPTY_PRODUCT, ...p, translations: tr });
    setEditorOpen(true);
  };

  const saveProduct = async () => {
    if (!editing.sku) { toast.error('SKU is required'); return; }
    try {
      const payload = {
        ...editing,
        melting_temp_c: editing.melting_temp_c === '' ? null : editing.melting_temp_c,
        working_temp_c: editing.working_temp_c === '' ? null : editing.working_temp_c,
        package_weight_kg: editing.package_weight_kg === '' ? null : editing.package_weight_kg,
        package_pieces: editing.package_pieces === '' ? null : editing.package_pieces,
        moq: editing.moq === '' ? null : editing.moq,
      };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post('/admin/products', payload);
      toast.success('Product saved');
      setEditorOpen(false);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/admin/products/${id}`);
    toast.success('Deleted');
    onReload();
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64s = await Promise.all(files.map(toBase64));
    setEditing(prev => ({ ...prev, images: [...prev.images, ...b64s] }));
  };

  const toggleArr = (key, val) => {
    setEditing(prev => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter(x => x !== val) : [...prev[key], val],
    }));
  };

  const setTr = (langCode, field, value) => {
    setEditing(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: { ...(prev.translations?.[langCode] || EMPTY_TR), [field]: value },
      },
    }));
  };

  const translateField = async (fromLang, field) => {
    const src = editing.translations?.[fromLang]?.[field];
    if (!src) { toast.error('Fill source first'); return; }
    toast.info('Translating…');
    try {
      for (const l of LANGUAGES.filter(x => x.code !== fromLang)) {
        if (editing.translations?.[l.code]?.[field]) continue;
        const { data } = await api.post('/admin/translate', { text: src, source_lang: fromLang, target_lang: l.code });
        setEditing(prev => ({
          ...prev,
          translations: {
            ...prev.translations,
            [l.code]: { ...(prev.translations?.[l.code] || EMPTY_TR), [field]: data.translation },
          },
        }));
      }
      toast.success('Translated');
    } catch { toast.error('Translation failed'); }
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const activeProducts = products.filter(p => p.active);
      const productIds = pdfSelected.length > 0 ? pdfSelected : activeProducts.map(p => p.id);
      const resp = await api.post('/admin/export-pdf', { lang: pdfLang, product_ids: productIds }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `catalog-${pdfLang}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
      setPdfOpen(false);
    } catch { toast.error('PDF export failed'); }
    finally { setPdfLoading(false); }
  };
  const togglePdfProduct = (id) => setPdfSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const downloadCsvTemplate = async () => {
    const resp = await api.get('/admin/products/csv-template', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'products-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/products/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import: ${data.created} created, ${data.updated} updated${data.errors.length ? `, ${data.errors.length} errors` : ''}`);
      if (data.errors.length) logger.warn('Import errors:', data.errors);
      onReload();
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif">Products</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadCsvTemplate} data-testid="csv-template">
            <FileDown className="w-4 h-4 mr-2" /> Template
          </Button>
          <label className="inline-flex">
            <Button variant="outline" size="sm" data-testid="csv-import-label" asChild>
              <span className="cursor-pointer">
                <Upload className="w-4 h-4 mr-2" /> {importing ? 'Importing…' : 'Import CSV'}
              </span>
            </Button>
            <input data-testid="csv-import-input" type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" />
          </label>
          <Button data-testid="admin-export-pdf" variant="outline" onClick={() => { setPdfSelected([]); setPdfOpen(true); }}>
            <FileDown className="w-4 h-4 mr-2" /> Export PDF
          </Button>
          <Button data-testid="admin-new-product" onClick={openNew}><Plus className="w-4 h-4 mr-2" /> New Product</Button>
        </div>
      </div>

      <div className="border rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Name (EN)</TableHead>
              <TableHead>Formats</TableHead>
              <TableHead>Colors</TableHead>
              <TableHead>MOQ</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#3D2817]/50">No products yet.</TableCell></TableRow>
            )}
            {products.map(p => (
              <TableRow key={p.id} data-testid={`admin-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                <TableCell>{p.translations?.en?.name || p.translations?.it?.name || '—'}</TableCell>
                <TableCell>{p.formats?.join(', ') || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {p.colors?.map(c => <span key={c} className="w-4 h-4 rounded-full border" style={{ background: COLOR_HEX[c] }} />)}
                  </div>
                </TableCell>
                <TableCell>{p.moq ?? '—'}</TableCell>
                <TableCell>{p.active ? <Badge className="bg-green-100 text-green-800">Yes</Badge> : <Badge variant="outline">No</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`admin-edit-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <Button data-testid={`admin-delete-${p.id}`} variant="ghost" size="icon" onClick={() => deleteProduct(p.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* PRODUCT EDITOR */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing.id ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
          <Tabs defaultValue="info">
            <TabsList>
              <TabsTrigger value="info">Info</TabsTrigger>
              <TabsTrigger value="specs">Tech Specs</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="tr">Translations</TabsTrigger>
            </TabsList>
            <TabsContent value="info" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>SKU *</Label><Input data-testid="editor-sku" value={editing.sku} onChange={e => setEditing({ ...editing, sku: e.target.value })} /></div>
                <div><Label>Category</Label><Input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} /></div>
                <div><Label>Color code (Pantone-like)</Label><Input placeholder="e.g. CQ1485" value={editing.color_code || ''} onChange={e => setEditing({ ...editing, color_code: e.target.value })} /></div>
                <div>
                  <Label>Micron range (powder only)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="editor-micron-min" type="number" placeholder="min · e.g. 2" value={editing.micron_min ?? ''} onChange={e => setEditing({ ...editing, micron_min: e.target.value === '' ? null : parseInt(e.target.value) })} />
                    <Input data-testid="editor-micron-max" type="number" placeholder="max · e.g. 5" value={editing.micron_max ?? ''} onChange={e => setEditing({ ...editing, micron_max: e.target.value === '' ? null : parseInt(e.target.value) })} />
                  </div>
                </div>
              </div>
              <div>
                <Label>Formats</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {AVAILABLE_FORMATS.map(f => (
                    <button key={f} type="button" onClick={() => toggleArr('formats', f)}
                      className={`px-3 py-1.5 text-xs uppercase border ${editing.formats.includes(f) ? 'bg-[#3D2817] text-white' : ''}`}>{f}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Colors</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {AVAILABLE_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => toggleArr('colors', c)} title={c}
                      className={`w-10 h-10 rounded-full border-2 ${editing.colors.includes(c) ? 'border-[#C5A059] scale-110' : 'border-[#E5D9C8]'}`}
                      style={{ background: COLOR_HEX[c] }} />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.featured} onChange={e => setEditing({ ...editing, featured: e.target.checked })} /> Featured
                </label>
              </div>
            </TabsContent>
            <TabsContent value="specs" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Melting Temp Range (°C)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="editor-melt-min" type="number" placeholder="min · e.g. 120" value={editing.melting_temp_min_c ?? ''} onChange={e => setEditing({ ...editing, melting_temp_min_c: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                    <Input data-testid="editor-melt-max" type="number" placeholder="max · e.g. 130" value={editing.melting_temp_max_c ?? ''} onChange={e => setEditing({ ...editing, melting_temp_max_c: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                  </div>
                </div>
                <div>
                  <Label>Working Temp Range (°C)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="editor-work-min" type="number" placeholder="min · e.g. 130" value={editing.working_temp_min_c ?? ''} onChange={e => setEditing({ ...editing, working_temp_min_c: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                    <Input data-testid="editor-work-max" type="number" placeholder="max · e.g. 160" value={editing.working_temp_max_c ?? ''} onChange={e => setEditing({ ...editing, working_temp_max_c: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                  </div>
                </div>
                <div><Label>Viscosity (cP)</Label><Input type="number" placeholder="e.g. 600000" value={editing.viscosity_cp ?? ''} onChange={e => setEditing({ ...editing, viscosity_cp: e.target.value === '' ? null : parseInt(e.target.value) })} /></div>
                <div><Label>Viscosity ref. Temp (°C)</Label><Input type="number" placeholder="e.g. 160" value={editing.viscosity_temp_c ?? ''} onChange={e => setEditing({ ...editing, viscosity_temp_c: e.target.value === '' ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>Melting Temp — single value (legacy)</Label><Input type="number" value={editing.melting_temp_c ?? ''} onChange={e => setEditing({ ...editing, melting_temp_c: e.target.value === '' ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>Working Temp — single value (legacy)</Label><Input type="number" value={editing.working_temp_c ?? ''} onChange={e => setEditing({ ...editing, working_temp_c: e.target.value === '' ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>Package Weight (kg)</Label><Input type="number" step="0.01" value={editing.package_weight_kg ?? ''} onChange={e => setEditing({ ...editing, package_weight_kg: e.target.value === '' ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>Package Pieces</Label><Input type="number" value={editing.package_pieces ?? ''} onChange={e => setEditing({ ...editing, package_pieces: e.target.value === '' ? null : parseInt(e.target.value) })} /></div>
                <div><Label>MOQ</Label><Input type="number" value={editing.moq ?? ''} onChange={e => setEditing({ ...editing, moq: e.target.value === '' ? null : parseInt(e.target.value) })} /></div>
                <div><Label>Sort order (lower = first)</Label><Input type="number" placeholder="e.g. 1" value={editing.sort_order ?? ''} onChange={e => setEditing({ ...editing, sort_order: e.target.value === '' ? null : parseInt(e.target.value) })} /></div>
              </div>
            </TabsContent>
            <TabsContent value="images" className="space-y-4 pt-4">
              <label className="border-2 border-dashed border-[#E5D9C8] rounded p-8 flex flex-col items-center gap-3 cursor-pointer hover:border-[#C5A059]">
                <ImageIcon className="w-8 h-8 text-[#3D2817]/40" />
                <span className="text-sm">Click to upload images</span>
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="editor-image-upload" />
              </label>
              <div className="grid grid-cols-4 gap-3">
                {editing.images.map((img, i) => (
                  <div key={img} className="relative aspect-square group">
                    <img src={img} alt="" className="w-full h-full object-cover rounded" />
                    <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, x) => x !== i) })} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="tr" className="space-y-4 pt-4">
              {LANGUAGES.map(l => (
                <div key={l.code} className="border rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest">{l.flag} {l.label}</Label>
                    {l.code === 'it' && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => translateField('it', 'name')}><Sparkles className="w-3 h-3 mr-1" /> Name→all</Button>
                        <Button variant="ghost" size="sm" onClick={() => translateField('it', 'tagline')}><Sparkles className="w-3 h-3 mr-1" /> Tag→all</Button>
                        <Button variant="ghost" size="sm" onClick={() => translateField('it', 'description')}><Sparkles className="w-3 h-3 mr-1" /> Desc→all</Button>
                      </div>
                    )}
                  </div>
                  <Input data-testid={`tr-name-${l.code}`} placeholder="Name" value={editing.translations?.[l.code]?.name || ''} onChange={e => setTr(l.code, 'name', e.target.value)} />
                  <Input placeholder="Tagline" value={editing.translations?.[l.code]?.tagline || ''} onChange={e => setTr(l.code, 'tagline', e.target.value)} />
                  <Textarea placeholder="Description" rows={3} value={editing.translations?.[l.code]?.description || ''} onChange={e => setTr(l.code, 'description', e.target.value)} />
                </div>
              ))}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button data-testid="editor-save" onClick={saveProduct}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF EXPORT */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Export Branded PDF Catalog</DialogTitle></DialogHeader>
          <div className="space-y-6 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Language</Label>
              <Select value={pdfLang} onValueChange={setPdfLang}>
                <SelectTrigger data-testid="pdf-lang-select" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest">Products</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setPdfSelected([])}>Include all</Button>
                  <Button variant="ghost" size="sm" onClick={() => setPdfSelected(activeProducts.map(p => p.id))}>Select all</Button>
                </div>
              </div>
              <p className="text-xs text-[#3D2817]/60 mb-3">
                {pdfSelected.length === 0 ? 'All active products will be included.' : `${pdfSelected.length} selected.`}
              </p>
              <div className="border rounded-sm max-h-72 overflow-y-auto">
                {activeProducts.map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-3 border-b hover:bg-[#F2ECE0] cursor-pointer">
                    <input type="checkbox" data-testid={`pdf-product-${p.id}`} checked={pdfSelected.includes(p.id)} onChange={() => togglePdfProduct(p.id)} />
                    <div>
                      <div className="font-mono text-xs text-[#3D2817]/50">{p.sku}</div>
                      <div className="font-serif">{p.translations?.en?.name || p.translations?.it?.name || p.sku}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)}>Cancel</Button>
            <Button data-testid="pdf-generate" onClick={exportPdf} disabled={pdfLoading}>{pdfLoading ? 'Generating…' : 'Generate PDF'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
